import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';
import {
  SIZE_BASED_ASSIGN_BIN_SIZES,
  BIN_SIZE_SHORT_LABEL,
  getBinSizeTranslationMap,
} from '../utils/binSizes';

/**
 * Tile colors derived from portal MUI theme (primary/secondary + slate/blue).
 * Green is reserved for Stocked bin status elsewhere — not used here.
 */
const getBinSizeSummaryColors = (theme) => {
  const { primary, secondary } = theme.palette;
  const emptyTint = 0.22;

  const tile = (activeBg, emptyBg, activeText, emptyText) => ({
    activeBg,
    emptyBg,
    activeText,
    emptyText,
  });

  return {
    'X-Small': tile(
      primary.dark,
      primary.light,
      primary.contrastText,
      primary.dark,
    ),
    Small: tile(
      primary.main,
      alpha(primary.main, emptyTint),
      '#fff',
      primary.main,
    ),
    Medium: tile(
      '#2563eb',
      alpha('#2563eb', emptyTint),
      '#fff',
      primary.dark,
    ),
    Large: tile(
      '#1e40af',
      alpha('#1e40af', emptyTint),
      '#fff',
      primary.dark,
    ),
    'Extra Large': tile(
      '#6a4c93',
      alpha('#6a4c93', emptyTint),
      '#fff',
      '#4a3470',
    ),
    'Medium Wide': tile(
      '#475569',
      alpha('#475569', emptyTint),
      '#fff',
      primary.dark,
    ),
    'Slim Wide': tile(
      secondary.main,
      alpha(secondary.main, 0.35),
      primary.dark,
      primary.main,
    ),
  };
};

/**
 * Compact bin availability grid for Canada size-based pre-assignment (sidebar).
 * Optional `headerAction` (e.g. search) sits on the title row so it never overlays the size tiles.
 */
const SizeBasedBinAvailabilitySummary = ({
  t,
  binCountsBySize = {},
  headerAction = null,
}) => {
  const theme = useTheme();
  const labelMap = getBinSizeTranslationMap(t);
  const colorMap = getBinSizeSummaryColors(theme);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        mb: 1,
        boxSizing: 'border-box',
      }}
    >
      {headerAction ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 1,
          }}
        >
          {headerAction}
        </Box>
      ) : null}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow:
            '0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.08)',
          p: 1.25,
        }}
      >
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.02em',
            lineHeight: 1.35,
            mb: 1,
          }}
        >
          {t('Unplugged.AvailableBins')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${SIZE_BASED_ASSIGN_BIN_SIZES.length}, minmax(0, 1fr))`,
            gap: 0.75,
            width: '100%',
          }}
        >
          {SIZE_BASED_ASSIGN_BIN_SIZES.map((label) => {
            const count =
              (binCountsBySize && binCountsBySize[label]) !== undefined
                ? binCountsBySize[label]
                : 0;
            const hasBins = count > 0;
            const fullLabel = labelMap[label] || label;
            const shortLabel = BIN_SIZE_SHORT_LABEL[label] || label;
            const colors = colorMap[label] || {
              activeBg: theme.palette.primary.main,
              emptyBg: theme.palette.primary.light,
              activeText: '#fff',
              emptyText: theme.palette.primary.dark,
            };

            return (
              <Tooltip key={label} title={fullLabel} arrow placement='top'>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 0.75,
                    px: 0.35,
                    borderRadius: 1.5,
                    border: 'none',
                    bgcolor: hasBins ? colors.activeBg : colors.emptyBg,
                    color: hasBins ? colors.activeText : colors.emptyText,
                    boxShadow: hasBins
                      ? '0 1px 3px rgba(0,0,0,0.12)'
                      : 'none',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    minWidth: 0,
                    minHeight: 44,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    component='div'
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: 'inherit',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {shortLabel}
                  </Typography>
                  <Typography
                    component='div'
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      lineHeight: 1.25,
                      color: 'inherit',
                      mt: 0.35,
                    }}
                  >
                    {count}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default SizeBasedBinAvailabilitySummary;



// WEBPACK FOOTER //
// ./src/components/SizeBasedBinAvailabilitySummary.js